import { browser } from '@src/slide/infrastructure/module/independent/agent/common';
import { IExtension } from '@tencent/ark-extensions';
import ArkLoader from '@src/js/common/ark-extensions';
import notificationsConfig from '@src/js/common/ark-extensions/dc-components/notifications/component-config';
import { tdw } from '@src/slide/infrastructure/module/dependent/report/offline-data-report/tdw-report';
import { onSlideAppReady, SlideDataServices } from '@src/slide/domain/slide-app/core/slide-data-services.core';
import {
    INotifyManager,
    INotifyManagerInjectable,
    INotifyManagerServiceDelegates,
    MODULE_NAME,
    NOTIFICATION_CENTER,
} from './notify-manager.interface';
import { NotifyRSBarModule } from '@src/slide/application/service/notify-manager/core/notify-rsbar-module';
import eventBus, { EventKeys } from '@src/slide/infrastructure/module/independent/event-emitter/instance';
import { OnlineEditServices } from '@src/slide/domain/slide-app/scenes/online/core/online-edit-services.core';

declare global {
    interface Window {
        AccountAvatar: any;
    }
}

export class NotifyManager implements INotifyManager {
    public notifyNumbers = 0;
    private module: IExtension | null = null;
    private services: INotifyManagerServiceDelegates;
    private injectable: INotifyManagerInjectable;

    public constructor() {
        const sdkReadCb = () => this.getNotifyNumbers();
        const receiveSocketMessageCb = (type, data) => this.handleNotifyMessage(type, data);

        onSlideAppReady(sdkReadCb);
        SlideDataServices.SDKDataServices.EventManager.promise()
            .then(({ socketEvents }) => socketEvents.onReceiveMessage(receiveSocketMessageCb));

        document.addEventListener('resetOuterUnreadNumberNoti', (e: any) => {
            this.resetNotifyNumbers(e.detail.number);
        });

        eventBus.on(EventKeys.HELP_CENTER_READY_DISPLAY, () => {
            // 阻止wheel事件，防止下面幻灯片收到事件切页
            document.getElementById(NOTIFICATION_CENTER)?.addEventListener('mousewheel', (e) => {
                e.stopPropagation();
            });
        });

        document.body.addEventListener('click', (e) => {
            if (e.target === document.getElementById('notification-dropdown-line')) {
                tdw({
                    opername: 'doc_menubar',
                    module: 'system_notification_button',
                    action: 'click',
                });
                this.show();
            }
        });
    }

    public setServiceDelegates(services: Partial<INotifyManagerServiceDelegates>) {
        this.services = {
            ...this.services,
            ...services,
        };
    }

    public setInject(injectable: INotifyManagerInjectable) {
        this.injectable = injectable;
    }

    public registerRightSideBar() {
        this.services.rightSideBarService.registerModule(new NotifyRSBarModule(this));
    }

    public showNotifyModule(DOMNode, onShow, onHide) {
        this.updateNotifyNode(DOMNode);
        const clientVars = SlideDataServices.ClientVarsService()?.getClientVars();
        let params: any = { from: 'slide' };
        // 原 domainId padId 已经收归到 ark 领域服务里面了，品类无需自己传了
        if (clientVars?.isCreator) {
            params = {
                ...params,
                selDocsTab: 2,
            };
        }
        this.loadNotifyModule(() => {
            this.module?.show({
                ...params,
                container: DOMNode,
                onCloseCb: () => {
                    onHide?.();
                },
            });
            onShow?.();
        });
    }

    public hideNotifyContainer() {
        this.module?.hide();
        const event = new CustomEvent('notification-reset-current-edit');
        document.dispatchEvent(event);
    }

    public show() {
        this.services.rightSideBarService.show(MODULE_NAME, undefined);
        this.clearAccountLineNotify();
    }

    /**
     * 更新通知css样式,通知模块比通用模块宽
     */
    private updateNotifyNode(DOMNode: HTMLElement) {
        if (DOMNode) {
            // 需要指定ID为‘notification-container’ 保证feature-components css 正常
            DOMNode.id = NOTIFICATION_CENTER;
            DOMNode.style.width = this.injectable.customNodeStyle.width ?? '325px';
            DOMNode.style.right = '0px';
            DOMNode.style.background = 'white';
            DOMNode.style.overflow = 'hidden';
        }
    }

    /**
     * 清除帐号列表下通知管理通知数
     */
    private clearNotifyNumbers() {
        this.notifyNumbers = 0;
        // 清空权限通知列表的红点数
        const event = new CustomEvent('setUnreadNotificationNumberNoti', { detail: { number: 0 } });
        document.dispatchEvent(event);
    }

    /**
     * 点击账户列表下的通知， 清除帐号列表下通知管理通知数
    */
    private clearAccountLineNotify() {
        const notifyEle = document.getElementById('notification-dropdown-line');
        const notifyNumberEle = notifyEle?.querySelector('.redPoint');
        const clientVars = SlideDataServices.ClientVarsService()?.getClientVars();
        const isCreator = clientVars?.isCreator;
        if (notifyNumberEle) {
            let params: any = { from: 'slide' };
            if (isCreator) {
                params = {
                    from: 'slide',
                    domainID: clientVars?.domainId,
                    localPadID: clientVars?.padId,
                };
            }
            const event = new CustomEvent('notification-update-list', params);
            document.dispatchEvent(event);
        }
        // 做个防护，如果不是我创建的文档，那么这里直接清除 不依赖push触发
        if (!isCreator) {
            this.clearNotifyNumbers();
        }
    }

    /**
     * 点击账户列表下的通知， 清除帐号列表下通知管理通知数
    */
    private async getNotifyNumbers() {
        const apiService = await OnlineEditServices.OnlineApiService.promise();
        apiService.getNotifyNumbers().then((res) => {
            const { data } = res;
            if (data && data.cgicode === 0 && data.retcode === 0) {
                if (data.unread_sum_count) {
                    const notifys = data.unread_sum_count;
                    this.resetNotifyNumbers(notifys);
                } else {
                    this.clearNotifyNumbers();
                }
            } else {
                console.log(`get notify numbers error${data}`);
            }
        })
            .catch((err) => {
                console.log(`get notify numbers error${err}`);
            });
    }

    /**
     * 更新外部通知显示，头像处通知数，只操作UI显示
     * @param  sendFlag  sendFlag === true 只清除外部($('#login-outside-redpoint'))红点; 否则也清除上一层（如列表页）的红点
     * @param  notifyNumbers 通知数，大于0显示
     */
    private updateOuterNotify(sendFlag, notifyNumbers) {
        if (window.AccountAvatar) {
            window.AccountAvatar.updateRedPoint(notifyNumbers);
        }
        if (browser.isMobile) {
            const event = new CustomEvent('setUnreadNotificationNumberNoti', { detail: { number: notifyNumbers } });
            document.dispatchEvent(event);
        }
        !sendFlag && this.updateServerNotify();
    }

    /**
     * 给服务端发送请求清未读数
     */
    private async updateServerNotify() {
        const apiService = await OnlineEditServices.OnlineApiService.promise();
        apiService.clearNotifyNumbers().then((res) => {
            console.log(`notify clear${res}`);
        })
            .catch((err) => {
                console.log(`notify clear error${err}`);
            });
    }

    /**
     * 修改未读数
     * @param isAdd   是否为添加未读
     * @param notifyNum 添加或者删除的未读个数
     */
    private updateNotifyNumbers(isAdd, notifyNum) {
        if (isAdd) {
            this.notifyNumbers += notifyNum;
        } else {
            this.notifyNumbers -= notifyNum;
        }
        if (this.notifyNumbers <= 0) {
            this.notifyNumbers = 0;
        }
        this.updateOuterNotify(isAdd, this.notifyNumbers);
        // 通知featureConponent里修改红点数
        const event = new CustomEvent('setUnreadNotificationNumberNoti', { detail: { number: this.notifyNumbers } });
        document.dispatchEvent(event);
    }

    /*
     * 重置未读数
     * @param notifyNum
    */
    private resetNotifyNumbers(notifyNum) {
        this.notifyNumbers = notifyNum;
        this.updateOuterNotify(true, this.notifyNumbers);
    }


    /**
     * 接收从socket传来的通知push
     * @param type 接收push类型 COMM_NOTICE 为通知类型
     * @param data 命令字 @name clearRedDot -> 清除通知 @name redDot -> 来了新的通知;
     */
    private handleNotifyMessage(type, data) {
        if (type !== 'COMM_NOTICE') {
            return;
        }
        if (this.dataHasNoBodyOrNotice(data)) {
            return;
        }
        const { notice } = data.body;
        const noticeType = data.body.notice.type;
        if (noticeType === 'push_message' && notice && notice.message) {
            const notifyData = JSON.parse(notice.message);
            if (notifyData?.msgType === 1) {
                // 清除红点的push,点击后清除红点
                if (notifyData.clearRedDot && notifyData.clearRedDot !== 0) {
                    this.updateNotifyNumbers(false, notifyData.clearRedDot);
                } else if (notifyData.redDot) { // 来了新通知
                    this.updateNotifyNumbers(true, notifyData.redDot);
                }
            }
        }
        this.handleNotifyComment(notice);
    }

    private handleNotifyComment(notice) {
        if (notice?.type === 'DOCCOMMENT_TYPE' && notice.payload?.comment?.length > 0 && notice.payload.op) {
            eventBus.emit(EventKeys.NOTICE_COMMENT_CHANGE, notice.payload);
        }
    }

    private async loadNotifyModule(cb) {
        if (!this.module) {
            this.module = await ArkLoader.extensionService.getExtension(notificationsConfig.name);
        }
        cb?.();
    }

    private dataHasNoBodyOrNotice(data) {
        if (!data?.body?.notice) {
            return true;
        }
        return false;
    }
}
